import React from 'react';
import { Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, Path, useFormContext, useWatch } from 'react-hook-form';
import { CurrencyInputField, FormRadioGroup, SelectField } from 'components/form';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { useSaleMode } from '../../../../../../context';
import { deskingActions, deskingSelectors } from '../../../../../../model';
import { determineMaxApprovalAmountNotification } from '../../../../../../lib';
import { existingProgramTypeOptions, styleFormRadioGroupProps } from '../../../../../../constants';
import {
  SaleType,
  BuilderViewType,
  FinanceProgramFormValues,
  CreateProposalFinanceExistingOptions,
} from '../../../../../../types';
import { get } from 'lodash';
import { resolveLatestProposal } from 'modules/creditSubmission';

interface Props {
  configuration?: Partial<Record<Path<FinanceProgramFormValues>, boolean>>;
  formDefaultValues?: FinanceProgramFormValues;
}

export const InitializeWorkflow = ({ configuration, formDefaultValues }: Props) => {
  const classes = useStyles();

  const dispatch = useDispatch();
  const { isCash, onChangeSaleType } = useSaleMode();

  const submissions = useSelector(deskingSelectors.submissions);
  const isSubmissionsLoading = useSelector(deskingSelectors.isSubmissionsLoading);
  const submissionsOptions = useSelector(deskingSelectors.submissionsOptions);
  const isFinanceDetailsEditable = useSelector(deskingSelectors.selectFinanceDetailsEditableStatus);

  const { control, resetField, setValue, reset } = useFormContext<FinanceProgramFormValues>();
  const [financeAmount, maxApprovalAmount] = useWatch({
    control,
    name: ['saleDetails.financeAmount', 'submission.maxApprovalAmount'],
  });

  const handleChangeWorkflowStatus = (value: string) => {
    if (value === CreateProposalFinanceExistingOptions.No) resetField('financeQuote');

    if (isCash) {
      setValue('saleDetails.saleType', SaleType.Financing);
      resetField('financeQuote.menu');
      onChangeSaleType(SaleType.Financing);
      dispatch(deskingActions.updateMenuBuilderViewType({ type: BuilderViewType.Finance }));
      dispatch(deskingActions.resetMenuBuilder());
    }
  };

  const handleResetDesking = () => {
    reset(formDefaultValues);
    onChangeSaleType(SaleType.Financing);
    dispatch(deskingActions.resetStructureAndMenuOptions());
    dispatch(deskingActions.setProposalDetailsForEdit(null));
  };

  const handleSelectSubmission = (selectedId: string) => {
    const submission = submissions[selectedId] ?? null;
    if (!submission) return;

    handleResetDesking();

    const isApprovedSubmissionV2 = submission.decision === 'Approved' && submission.proposals.length;
    if (isApprovedSubmissionV2) {
      const proposal = resolveLatestProposal(submission.proposals);
      dispatch(deskingActions.setProposalDetailsForEdit({ id: proposal!.proposalId!, saleType: proposal!.saleType! }));
    } else {
      setValue('submission.selectedSubmissionId', submission.id);
      setValue('submission.maxApprovalAmount', submission.approvedAmount);
    }
    dispatch(deskingActions.selectSubmission({ submission }));
  };

  return (
    <>
      <RenderWithCondition condition={!!get(configuration, 'submission.selectedSubmissionId', true)}>
        <SelectField
          control={control}
          label="Select Submission"
          options={submissionsOptions}
          isLoading={isSubmissionsLoading}
          name="submission.selectedSubmissionId"
          extraAction={selectedId => handleSelectSubmission(selectedId as string)}
        />
      </RenderWithCondition>

      <RenderWithCondition condition={!!get(configuration, 'submission.maxApprovalAmount', true)}>
        <CurrencyInputField
          control={control}
          label="Max Approval Amount"
          name="submission.maxApprovalAmount"
          notify={determineMaxApprovalAmountNotification(financeAmount, maxApprovalAmount)}
          textFieldProps={{
            placeholder: 'Enter amount...',
            disabled: !isFinanceDetailsEditable,
          }}
        />
      </RenderWithCondition>

      <Box className={classes.container}>
        <Typography variant="subtitle2" component="span">
          Use Saved Program?
        </Typography>

        <Controller
          control={control}
          name="financeQuote.createProposalWithExistingProgram"
          render={({ field }) => (
            <FormRadioGroup
              row
              options={existingProgramTypeOptions}
              {...styleFormRadioGroupProps}
              {...field}
              disabled={!isFinanceDetailsEditable}
              onChange={(event, value) => {
                handleChangeWorkflowStatus(value);
                field.onChange(event, value);
              }}
            />
          )}
        />
      </Box>
    </>
  );
};

const useStyles = makeStyles({
  container: {
    gap: '1rem',
    display: 'flex',
    alignItems: 'center',
  },
});
